import React from 'react';
import clsx from 'clsx';

import * as Styled from './OBannerText.styles';
import {
  OBannerTextProps,
  OBannerTextContentAlignment,
  OBannerTextParagraph,
  OBannerTextButton,
  // OBannerTextBrandElement,
  OBannerTextAccentLine,
  OBannerTextBrandElement,
  OBannerTextIconBrandElement,
  OBannerTextLogoBrandElement,
  OBannerTextImageBrandElement,
  OBannerTextI18nProperties,
} from './OBannerText.types';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  AAccentLineSize,
  ABodySize,
  ADivider,
  ADividerOrientation,
  AHeaderSize,
  AHeaderTag,
  AIconSize,
  ALinkStandaloneSize,
  MBrandElement,
  MBrandElementType,
  MButton,
  MButtonSize,
  MButtonType,
  MLogoContainerSize,
  MParagraph,
  MParagraphAlignment,
  MTextBlockStandard,
  MTextBlockStandardAlignment,
} from '@marriott/mi-web-ui-kit';

const COMPONENT_NAME = 'o-mi-web-ui-kit-OBannerText';
const TEST_ID = 'mi-web-ui-kit-OBannerText';

export const OBannerText: React.FC<OBannerTextProps> = ({
  alignment = OBannerTextContentAlignment.Center,
  mBrandElement,
  mTextBlockStandard,
  mParagraph,
  mParagraph2,
  mButton,
  mButton2,
  aAccentLine,
  className,
  i18nProperties,
}) => {
  const [paragraph, paragraph2]: Array<OBannerTextParagraph | void> = [mParagraph, mParagraph2].map(p =>
    canRenderParagraph(p) ? p : undefined
  );
  const [button, button2]: Array<OBannerTextButton | void> = [mButton, mButton2].map(b =>
    canRenderButton(b) ? b : undefined
  );

  const showDoubleColumn =
    (paragraph !== undefined || button !== undefined) && (paragraph2 !== undefined || button2 !== undefined);
  const showAnyColumns =
    paragraph !== undefined || button !== undefined || paragraph2 !== undefined || button2 !== undefined;

  return (
    <Styled.Wrapper
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      className={clsx('container d-flex align-items-center py-5', className, {
        'justify-content-center': alignment === OBannerTextContentAlignment.Center,
      })}
    >
      <div className="col-12 col-lg-10 px-0">
        {mBrandElement && renderBrandWrapper(alignment, mBrandElement)}

        {mTextBlockStandard?.aHeader?.text && (
          <Styled.TextBlockWrapper>
            <MTextBlockStandard
              eyebrowText={mTextBlockStandard.aEyebrow?.eyebrowText ?? ''}
              headerText={mTextBlockStandard.aHeader.text}
              headerSize={(mTextBlockStandard.aHeader.size as AHeaderSize) ?? AHeaderSize.TitleM}
              headerTag={(mTextBlockStandard.aHeader.headerTag as AHeaderTag) ?? AHeaderTag.h2}
              hasAccentLine={aAccentLine?.show}
              accentLineSize={aAccentLine?.size as AAccentLineSize}
              alignment={
                alignment === OBannerTextContentAlignment.Center
                  ? MTextBlockStandardAlignment.Center
                  : MTextBlockStandardAlignment.Left
              }
            />
          </Styled.TextBlockWrapper>
        )}

        {showAnyColumns ? (
          showDoubleColumn ? (
            <Styled.ParagraphsContainer className="double justify-content-between">
              {renderParagraph({
                alignment,
                ...(paragraph ? { paragraph } : {}),
                ...(button ? { button } : {}),
                testId: `${TEST_ID}-paragraph-column-1`,
                i18nProperties,
              })}

              <div className="d-flex d-lg-none justify-content-center">
                <ADivider orientation={ADividerOrientation.Horizontal} />
              </div>

              <div className="d-none d-lg-flex justify-content-center">
                <ADivider orientation={ADividerOrientation.Vertical} />
              </div>

              {renderParagraph({
                alignment,
                ...(paragraph2 ? { paragraph: paragraph2 } : {}),
                ...(button2 ? { button: button2 } : {}),
                testId: `${TEST_ID}-paragraph-column-2`,
                i18nProperties,
              })}
            </Styled.ParagraphsContainer>
          ) : (
            <Styled.ParagraphsContainer className="single">
              {renderParagraph({
                alignment,
                ...(paragraph ? { paragraph: paragraph } : {}),
                ...(paragraph2 ? { paragraph: paragraph2 } : {}),
                ...(button ? { button: button } : {}),
                ...(button2 ? { button: button2 } : {}),
                testId: `${TEST_ID}-paragraph-column-1`,
                i18nProperties,
              })}
            </Styled.ParagraphsContainer>
          )
        ) : (
          <></>
        )}
      </div>
    </Styled.Wrapper>
  );
};

function renderBrandWrapper(alignment: OBannerTextContentAlignment, brandElement: OBannerTextBrandElement) {
  if (isLogoBrandElement(brandElement)) {
    return (
      <Styled.BrandWrapper alignment={alignment}>
        <MBrandElement
          type={brandElement?.type}
          brand={brandElement?.mLogoContainer?.aLogo32?.brand}
          logoSize={MLogoContainerSize.M}
          logoColor={'background-aware'}
        />
      </Styled.BrandWrapper>
    );
  }

  if (isIconBrandElement(brandElement)) {
    return (
      <Styled.BrandWrapper alignment={alignment}>
        <MBrandElement
          type={brandElement?.type}
          icon={brandElement?.aIcon?.icon}
          iconSize={(brandElement?.aIcon?.size as AIconSize) ?? AIconSize.XXL}
        />
      </Styled.BrandWrapper>
    );
  }

  if (isImageBrandElement(brandElement)) {
    return (
      <Styled.BrandWrapper alignment={alignment}>
        <MBrandElement type={brandElement?.type} image={brandElement?.aImage.image} />
      </Styled.BrandWrapper>
    );
  }
  return <></>;
}

function renderParagraph({
  alignment,
  paragraph,
  button,
  testId,
  i18nProperties,
}: {
  alignment: OBannerTextContentAlignment;
  paragraph?: OBannerTextParagraph;
  button?: OBannerTextButton;
  testId?: string;
  i18nProperties?: OBannerTextI18nProperties;
}) {
  return (
    <Styled.ParagraphItem data-testid={testId}>
      {paragraph && (
        <MParagraph
          alignment={
            alignment === OBannerTextContentAlignment.Center ? MParagraphAlignment.Center : MParagraphAlignment.Left
          }
          truncated={paragraph?.truncated === 'true'}
          expanded={paragraph?.expanded === 'true'}
          bodySize={ABodySize.M}
          truncationLinkSize={ALinkStandaloneSize.Medium}
          i18nProperties={i18nProperties}
        >
          {paragraph?.aBody?.bodyText ?? ''}
        </MParagraph>
      )}
      {button && (
        <Styled.ButtonWrapper alignment={alignment}>
          <MButton
            buttonText={button.buttonText ?? undefined}
            variant={(button.type as MButtonType) ?? MButtonType.Secondary}
            size={MButtonSize.S}
            isExternal={button.openInANewTab === 'true'}
            href={button.ctaLink ?? undefined}
          />
        </Styled.ButtonWrapper>
      )}
    </Styled.ParagraphItem>
  );
}

function isLogoBrandElement(brandElement?: OBannerTextBrandElement): brandElement is OBannerTextLogoBrandElement {
  return (
    brandElement != null &&
    brandElement?.type === MBrandElementType.Logo &&
    brandElement?.mLogoContainer?.aLogo32?.brand != null
  );
}

function isIconBrandElement(brandElement?: OBannerTextBrandElement): brandElement is OBannerTextIconBrandElement {
  return brandElement != null && brandElement?.type === MBrandElementType.Icon && brandElement?.aIcon?.icon != null;
}

function isImageBrandElement(brandElement?: OBannerTextBrandElement): brandElement is OBannerTextImageBrandElement {
  return brandElement != null && brandElement?.type === MBrandElementType.Image && brandElement?.aImage?.image != null;
}

function canRenderParagraph(p: unknown): p is OBannerTextParagraph {
  return (
    p != null &&
    typeof p === 'object' &&
    'aBody' in p &&
    p.aBody != null &&
    typeof p.aBody === 'object' &&
    'bodyText' in p.aBody &&
    typeof p.aBody.bodyText === 'string'
  );
}

function canRenderButton(b: unknown): b is OBannerTextButton {
  return (
    b != null &&
    typeof b === 'object' &&
    'ctaLink' in b &&
    b.ctaLink != null &&
    'buttonText' in b &&
    b.buttonText != null
  );
}

export { OBannerTextContentAlignment, TEST_ID as O_BANNER_TEXT_TEST_ID };
export type { OBannerTextProps, OBannerTextAccentLine, OBannerTextI18nProperties };
